import type { Session, User } from '@supabase/supabase-js';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type { AppCode, Permission, Role } from './permissions';

/**
 * `erro` existe porque a falta dele era o travamento.
 *
 * A carga da conta é uma sequência de consultas. Se qualquer uma falhasse, a
 * promessa era engolida por um `void` e o estado ficava em `loading` para
 * sempre: a tela "Carregando sua conta..." não voltava mais, e a única saída
 * era recarregar. Agora falha vira estado, e estado vira tela com botão.
 */
export type AuthStatus = 'loading' | 'unauthed' | 'no-org' | 'suspended' | 'ready' | 'erro';

export type Org = {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  /** Marca do cliente. O painel pinta o destaque com ela (ver ui/marca.ts). */
  logo_url?: string | null;
  logo_dark?: string | null;
  accent?: string | null;
  accent_alt?: string | null;
  brand_theme?: string | null;
  brand_radius?: string | null;
};

type Ctx = {
  status: AuthStatus;
  user: User | null;
  profileName: string;
  /** Organização ativa, vinda do claim do JWT. Nunca do cliente. */
  org: Org | null;
  orgs: Org[];
  role: Role | null;
  installedApps: AppCode[];
  permissions: Permission[];
  /** Equipe Yard: atravessa organizações, opera o console de clientes. */
  adminPlataforma: boolean;
  /**
   * Organização que a Yard está visitando para dar suporte ou demonstrar.
   * Quando preenchida, `org` é ela: a sessão inteira responde como o cliente.
   */
  visitando: Org | null;
  /** true quando o Auth Hook não está ligado e a organização veio do banco. */
  hookAusente: boolean;
  refresh: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  /** Entra na conta do cliente. Só a equipe Yard; o banco confere de novo. */
  entrarNaOrg: (orgId: string) => Promise<void>;
  sairDaOrg: () => Promise<void>;
  signOut: () => Promise<void>;
};

/**
 * Exportado para que a galeria e a folha de telas montem uma sessão de mentira.
 *
 * Não é porta dos fundos: quem consome é código que só existe fora de produção,
 * e o contexto sozinho não dá acesso a nada - a defesa de verdade é a RLS no
 * banco, e ela não olha para este objeto. Sem isto, uma tela que usa `<Link>`
 * (que lê o slug do cliente daqui) não pode ser desenhada fora do aplicativo.
 */
export const AuthCtx = createContext<Ctx | null>(null);
export type { Ctx as ContextoDeSessao };

/**
 * O hook está ligado quando o token traz a chave `org_id`, mesmo vazia. Vazia
 * é resposta legítima: a equipe Yard não pertence a organização nenhuma. Antes
 * media-se pela ausência de valor, e aí quem não tinha organização via para
 * sempre um aviso pedindo para configurar algo que já estava configurado.
 *
 * Só isto ainda lê o claim direto: é sobre o Auth Hook estar ligado no
 * PROJETO, não sobre qual é a organização ativa - isso último (com ou sem o
 * hook) é `meu_contexto()` (migração 0088, L1) quem decide, do lado do
 * banco, com as mesmas funções que a RLS usa.
 */
function hookLigado(session: Session | null): boolean {
  const meta = session?.user.app_metadata as Record<string, unknown> | undefined;
  return !!meta && 'org_id' in meta;
}

/** O formato de retorno de `meu_contexto()` (migração 0088, L1). */
type ContextoRpc = {
  sem_sessao?: boolean;
  profile_name: string | null;
  eh_admin: boolean;
  orgs: Org[];
  org: Org | null;
  visitando: Org | null;
  role: string | null;
  apps: AppCode[];
  permissions: Permission[];
  status: Extract<AuthStatus, 'ready' | 'no-org' | 'suspended'>;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [session, setSession] = useState<Session | null>(null);
  // A sessão começa nula tanto "ainda não perguntamos ao SDK" quanto "perguntamos
  // e não há ninguém logado" - os dois casos têm o mesmo valor. Esta flag separa
  // os dois: sem ela, o efeito que hidrata não tem como saber que o primeiro
  // `null` (estado inicial do useState) já é uma resposta válida do SDK.
  const [sessaoResolvida, setSessaoResolvida] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [org, setOrg] = useState<Org | null>(null);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [installedApps, setInstalledApps] = useState<AppCode[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [adminPlataforma, setAdminPlataforma] = useState(false);
  const [visitando, setVisitando] = useState<Org | null>(null);
  const [hookAusente, setHookAusente] = useState(false);
  const [role, setRoleState] = useState<Role | null>(null);

  /**
   * Cada carga recebe um número. Duas podem estar no ar ao mesmo tempo (a
   * inicial e a de um token renovado, por exemplo), e a que volta primeiro nem
   * sempre é a mais nova. Sem isto, a resposta atrasada de uma carga antiga
   * sobrescrevia a recente e a tela voltava para um estado que já não valia.
   */
  const geracao = useRef(0);

  // B7: o cache do React Query é por usuário, não só por organização. Token
  // expira, outra pessoa entra na mesma aba, e sem isto os dados da pessoa
  // anterior alimentavam a tela por um instante - e para as consultas sem
  // tenant (`useListGlobal`, `is_platform_admin` do console) nem só um
  // instante: a chave delas nunca leva o usuário, então nunca invalidam
  // sozinhas (ver `data/db.ts`). `undefined` é "ainda não vimos ninguém" -
  // sem isso, a primeira sessão de verdade pareceria uma troca e limparia um
  // cache que já estava vazio (inofensivo, mas sem sentido).
  const qc = useQueryClient();
  const usuarioAnterior = useRef<string | null | undefined>(undefined);

  const hydrate = useCallback(async (s: Session | null) => {
    const minha = ++geracao.current;
    const atual = () => geracao.current === minha;
    if (!supabase) {
      setStatus('unauthed');
      return;
    }
    if (!s) {
      setStatus('unauthed');
      setOrg(null);
      setOrgs([]);
      setInstalledApps([]);
      setPermissions([]);
      return;
    }

    // Só inspeciona o token que o SDK já tem na mão - não é ida à rede, e
    // não muda com a organização ativa (é sobre o Auth Hook estar ligado ou
    // não, uma propriedade do PROJETO, não da sessão).
    setHookAusente(!hookLigado(s));

    // L1 (auditoria 2026-08-31): eram três rodadas de rede em sequência -
    // cinco consultas em paralelo, depois (só sabendo a organização ativa)
    // mais duas, às vezes uma terceira para buscar a organização visitada
    // quando ela não estava na lista de vínculos. `meu_contexto()` (migração
    // 0088) faz tudo isso do lado do banco, com as MESMAS funções que a RLS
    // já usa para decidir organização ativa e papel (`current_org()`,
    // `current_role_name()`) - não há como esta leitura discordar da RLS
    // sobre quem pode o quê.
    // Sem `.single()`/`.maybeSingle()`: `meu_contexto()` devolve `jsonb`
    // puro (não `returns table`), então o PostgREST já entrega o valor
    // direto, sem embrulhar num array de uma linha - `.single()` aqui
    // esperaria por esse array e quebraria.
    const { data, error: erroCtx } = await supabase.rpc('meu_contexto');
    const ctx = data as ContextoRpc | null;

    // B4: falha de rede não pode virar uma afirmação sobre o vínculo da
    // pessoa - vira `'erro'` (a tela com botão de tentar de novo), nunca uma
    // conclusão sobre não ter organização.
    if (erroCtx) throw erroCtx;

    // B5: a guarda de geração protege a escrita depois do `await`. Uma carga
    // velha que perdesse a corrida para uma mais nova não pode escrever por
    // cima dela.
    if (!atual()) return;

    if (!ctx || ctx.sem_sessao) {
      // O token que o SDK tinha na mão já não vale mais para o banco
      // (`auth.uid()` nulo do lado de lá) - mesmo efeito prático de não
      // haver sessão.
      setStatus('unauthed');
      setOrg(null);
      setOrgs([]);
      setInstalledApps([]);
      setPermissions([]);
      return;
    }

    setAdminPlataforma(ctx.eh_admin);
    setProfileName(ctx.profile_name || s.user.email || '');
    setOrgs(ctx.orgs);
    setOrg(ctx.org);
    setVisitando(ctx.visitando);
    setRoleState((ctx.role as Role) ?? null);
    setInstalledApps(ctx.apps);
    setPermissions(ctx.permissions);
    setStatus(ctx.status);
  }, []);

  // Assina o SDK e resolve a sessão inicial. Este efeito não faz nenhuma
  // consulta própria: só troca `session`/`sessaoResolvida`. Quem hidrata é o
  // efeito seguinte, que observa essa troca.
  useEffect(() => {
    if (!supabase) {
      setStatus('unauthed');
      return;
    }
    let alive = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      setSessaoResolvida(true);
    });

    // B3: o callback só troca estado local - nenhuma consulta ao Supabase
    // parte daqui. O SDK segura o lock interno de auth durante a execução
    // deste callback; uma consulta disparada de dentro dele espera o mesmo
    // lock e pendura até o relógio de 20s do efeito seguinte desistir. É a
    // armadilha que a própria documentação do Supabase marca: nada de `await`
    // em Supabase dentro do `onAuthStateChange`.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setSessaoResolvida(true);
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // A hidratação mora aqui, fora do lock do SDK, disparada pela troca de
  // `session` - a porta que o SDK controla (login, logout, token renovado).
  // `entrarNaOrg`/`sairDaOrg` chamam `hydrate` direto, sem passar por aqui
  // (ver comentário nelas, abaixo): a visita não muda o token, então não há
  // troca de `session` para este efeito observar.
  useEffect(() => {
    if (!supabase || !sessaoResolvida) return;
    let alive = true;

    // B7: usuário mudou -> cache de outra pessoa fora. Comparar contra a
    // sessão, não contra o resultado do hydrate, porque o cache tem de sumir
    // ANTES de qualquer consulta da carga nova ir para o ar - senão a
    // resposta da pessoa anterior, ainda em voo, pode gravar numa chave que a
    // carga nova também usa.
    const usuarioNovo = session?.user.id ?? null;
    if (usuarioAnterior.current !== undefined && usuarioAnterior.current !== usuarioNovo) {
      qc.clear();
    }
    usuarioAnterior.current = usuarioNovo;

    // Falha na carga vira tela com botão, e não espera eterna. Era aqui que o
    // `void` engolia a rejeição e deixava o "Carregando sua conta..." de pé
    // para sempre.
    hydrate(session).catch(() => {
      if (alive) setStatus('erro');
    });

    // Rede que não responde nem falha existe: pedido que fica pendurado não
    // rejeita, e sem isto a espera não termina nunca. Vinte segundos é bem mais
    // do que a carga leva, e bem menos do que a paciência de quem espera.
    const relogio = window.setTimeout(() => {
      if (!alive) return;
      setStatus((s) => (s === 'loading' ? 'erro' : s));
    }, 20_000);

    return () => {
      alive = false;
      window.clearTimeout(relogio);
    };
  }, [session, sessaoResolvida, hydrate, qc]);

  const refresh = useCallback(async () => {
    if (!supabase) return;
    // B2: ler o `error`. Em falha - "Invalid Refresh Token: Already Used" é
    // comum com rotação de token e duas abas - a sessão atual continua válida
    // até expirar de verdade; derrubá-la aqui deslogava exatamente na hora de
    // trocar de conta, porque `switchOrg` passa por este método.
    const { data, error } = await supabase.auth.refreshSession();
    if (error) return;
    // B6: só troca a sessão. Hidratar de novo aqui, na mão, era a segunda
    // carga completa: `refreshSession()` já dispara `TOKEN_REFRESHED`, que por
    // si só re-hidrata pelo listener acima. Uma única porta de entrada.
    setSession(data.session);
  }, []);

  const switchOrg = useCallback(
    async (orgId: string) => {
      if (!supabase) return;
      const { error } = await supabase.rpc('set_active_org', { p_org: orgId });
      if (error) throw new Error(error.message);
      // Com o hook ligado, o claim novo vem no token renovado. Sem ele, a
      // reidratação já pega a organização pela consulta ao banco.
      await refresh();
    },
    [refresh],
  );

  const entrarNaOrg = useCallback(
    async (orgId: string) => {
      if (!supabase) return;
      const { error } = await supabase.rpc('yard_entrar_na_org', { p_org: orgId });
      if (error) throw new Error(error.message);
      // B2: não pede token novo. Diferente de `switchOrg`, a visita não
      // depende do claim do JWT - `yard_org_visitada()` lê `impersonando`
      // direto do banco (0019), e `current_org()` a coloca na frente de
      // qualquer claim. Um `refreshSession()` aqui seria só uma volta de rede
      // a mais, sem mudar nada que o hydrate fosse ler diferente.
      await hydrate(session);
    },
    [hydrate, session],
  );

  const sairDaOrg = useCallback(async () => {
    if (!supabase) return;
    const { error } = await supabase.rpc('yard_sair_da_org');
    if (error) throw new Error(error.message);
    // B2: mesmo motivo de `entrarNaOrg` - sair da visita também não muda o
    // claim, só o que `impersonando` diz no banco.
    await hydrate(session);
  }, [hydrate, session]);

  const signOut = useCallback(async () => {
    // A4: fecha a visita antes de sair. Sem isto, `impersonando` sobrevivia
    // ao logout - fechar a aba, dormir o notebook, qualquer saída que não
    // passasse pelo botão "Sair da conta" deixava a marca de pé, e entrar de
    // novo dias depois caía direto dentro da conta do último cliente
    // visitado (a migration 0074 pôs um prazo de duas horas nisso, mas sair
    // de verdade não devia depender só do relógio). Em `try/catch`: sair da
    // própria conta não pode falhar por causa de uma RPC que não respondeu.
    try {
      await sairDaOrg();
    } catch {
      // Sem tratamento - o logout segue mesmo se isto falhar.
    }
    await supabase?.auth.signOut();
    setStatus('unauthed');
  }, [sairDaOrg]);

  const value = useMemo<Ctx>(
    () => ({
      status,
      user: session?.user ?? null,
      profileName,
      org,
      orgs,
      role,
      installedApps,
      permissions,
      adminPlataforma,
      visitando,
      hookAusente,
      refresh,
      switchOrg,
      entrarNaOrg,
      sairDaOrg,
      signOut,
    }),
    [status, session, profileName, org, orgs, role, installedApps, permissions, adminPlataforma, visitando, hookAusente, refresh, switchOrg, entrarNaOrg, sairDaOrg, signOut],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Ctx {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}

/**
 * Espelha a permissão para melhorar a experiência. Não autoriza: quem decide é
 * a RLS no banco (documento 17, regra de ouro).
 */
export function useCan(permission?: Permission): boolean {
  const { permissions, installedApps } = useAuth();
  if (!permission) return true;
  const app = permission.split('.')[0]?.toUpperCase();
  if (app !== 'PLATFORM' && !installedApps.includes(app as AppCode)) return false;
  return permissions.includes(permission);
}

export function Can({ perm, children }: { perm: Permission; children: ReactNode }) {
  return useCan(perm) ? <>{children}</> : null;
}
